import React, { useEffect, useMemo, useState } from "react"
import { useUser } from "util/UserContext"
import { ReactComponent as IconContentPolicy } from "assets/icon-content-policy.svg"
import { ReactComponent as IconUserAgreement } from "assets/icon-user-agreement.svg"
import { ReactComponent as GraphExploreIcon } from "assets/icon-graph-explorer.svg"
import { useNavigate } from "react-router-dom"
import { ReactComponent as SocieteaBrandMarkIcon } from "assets/societea_brandmark_icon.svg"
import { getRecentIssues, RecentIssue, removeRecentIssue } from "util/recentIssuesUtils"
import { getIssueRankings, IssueRanking } from "util/userUtils"
import DetailPanel from "ui/DetailPanel"
import EntityAvatar from "components/Entities/EntityAvatar"
import { Issue } from "util/constants"
import { LAYER_TOKENS } from "ui/layers"
import {
    Award,
    Home,
    ListTodo,
    MessageSquarePlus,
    ShieldCheck,
    Flag,
    Bug,
    Scale,
    BriefcaseBusiness,
    MessageCircleQuestion,
    Handshake,
    FlaskConical,
    Clapperboard,
} from "lucide-react"
import { getTaskUiStrings } from "i18n/taskUi"
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi"
import { getNavigationUiStrings, resolveNavigationLocale } from "i18n/navigationUi"
import { getAdBreakHref } from "util/adBreakUrl"
import { useAwardGivingBudgetQuery, usePromotionCreditBalanceQuery } from "hooks/queries/useAwardBalanceQuery"
import { useBodyScrollLock } from "ui/useBodyScrollLock"

interface SideNavBarProps {
    isOpen: boolean
    toggleDrawer: (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => void
    onClick: () => void
}

interface BaseNavItem {
    id: string
    label: string
    renderIcon: () => React.ReactNode
    gapClassName?: string
    desktopPaddingClassName?: string
    mobilePaddingClassName?: string
}

interface InternalNavItem extends BaseNavItem {
    to: string
    href?: never
}

interface ExternalNavItem extends BaseNavItem {
    href: string
    to?: never
}

type NavItem = InternalNavItem | ExternalNavItem
type NavItemDefinition = Omit<InternalNavItem, "label"> | Omit<ExternalNavItem, "label">
type NavVariant = "desktop" | "mobile"
const APP_BAR_HEIGHT_PX = 56

const PRIMARY_NAV_ITEMS: ReadonlyArray<Omit<InternalNavItem, "label">> = [
    {
        id: "home",
        to: "/",
        renderIcon: () => <Home size={20} />
    },
    {
        id: "graph",
        to: "/graph",
        renderIcon: () => <GraphExploreIcon className="size-5" />
    },
    {
        id: "communitea",
        to: "/communitea",
        renderIcon: () => <Handshake size={20} />
    },
    {
        id: "feedback",
        to: "/user-feedback",
        renderIcon: () => <MessageSquarePlus size={20} />
    }
]

const ADMIN_NAV_ITEMS: ReadonlyArray<Omit<InternalNavItem, "label">> = [
    {
        id: "admin-home",
        to: "/admin",
        renderIcon: () => <ShieldCheck size={20} />
    },
    {
        id: "admin-reports",
        to: "/admin/reports",
        renderIcon: () => <Flag size={20} />
    },
    {
        id: "admin-feedback",
        to: "/admin/feedback",
        renderIcon: () => <Bug size={20} />
    }
]

const RESOURCE_NAV_ITEMS: ReadonlyArray<Omit<ExternalNavItem, "label">> = [
    {
        id: "about-societea",
        href: "https://www.societea.io",
        renderIcon: () => <SocieteaBrandMarkIcon className="size-6 stroke-current fill-none" />,
        gapClassName: "gap-2",
        desktopPaddingClassName: "px-3 py-2"
    },
    {
        id: "get-help",
        href: "mailto:support@societea.io",
        renderIcon: () => <MessageCircleQuestion size={20} />
    },
    {
        id: "build-societea",
        href: "https://www.societea.io/careers",
        renderIcon: () => <BriefcaseBusiness size={20} />
    }
]

const PRIVACY_NAV_ITEMS: ReadonlyArray<NavItemDefinition> = [
    {
        id: "content-policy",
        href: "https://www.societea.io/policies/content-policy",
        renderIcon: () => <IconContentPolicy className="size-5" />
    },
    {
        id: "privacy-policy",
        to: "/policies/privacy-policy",
        renderIcon: () => <Scale size={20} />
    },
    {
        id: "user-agreement",
        href: "https://www.societea.io/policies/user-agreement",
        renderIcon: () => <IconUserAgreement className="size-5" />
    }
]

const buildFallbackIssueEntity = (recentIssue: RecentIssue): Issue => ({
    id: recentIssue.id,
    id_short: `${recentIssue.id}`,
    name: recentIssue.name,
    cname: recentIssue.cname,
    subject_id: recentIssue.id,
    subject_cname: recentIssue.cname,
    wikipedia_link: "",
    topic_id: recentIssue.id,
    topic_cname: "",
    topic_wikipedia_link: "",
    created_at: "",
    att_hash: "",
    issue_id: recentIssue.id,
    scope: "",
    status: "",
    description: recentIssue.description ?? "",
    inactive_at: "",
    is_latest: true,
    user_id: 0,
    updated_at: "",
    upvotes: 0,
    downvotes: 0,
    vote_count: 0,
    user_display_name: "",
    username: "",
    reference_link: "",
    user_vote: "",
    rel_type: "",
    properties: {
        type: recentIssue.type ?? "",
        subject_properties: {
            thumbnail: { source: recentIssue.thumbnail ?? "" },
            wiki_parse_wikibase_shortdesc: recentIssue.description ?? ""
        }
    },
    properties_public: {},
    attributes: null
})

const isExternalNavItem = (item: NavItem): item is ExternalNavItem => "href" in item

const SideNavBar = ({ isOpen, toggleDrawer, onClick }: SideNavBarProps) => {
    const { currentUser } = useUser()
    const navigate = useNavigate()
    const [recentIssues, setRecentIssues] = useState<RecentIssue[]>([])
    const [topIssues, setTopIssues] = useState<IssueRanking[]>([])
    const taskUi = useMemo(() => getTaskUiStrings(), [])
    const storyUi = useMemo(() => getStoryUiStrings(resolveStoryLocale()), [])
    const navigationUi = useMemo(() => getNavigationUiStrings(resolveNavigationLocale()), [])
    const awardBudgetQuery = useAwardGivingBudgetQuery(Boolean(currentUser))
    const promotionCreditQuery = usePromotionCreditBalanceQuery(Boolean(currentUser))
    const awardBudget = awardBudgetQuery.data?.balance ?? 0
    const promotionCredits = promotionCreditQuery.data?.balance ?? 0
    const awardBudgetTitle = storyUi.awardGivingBudgetTitle.replace("{count}", String(awardBudget))
    const promotionCreditsTitle = storyUi.promotionCreditsTitle.replace("{count}", String(promotionCredits))
    useBodyScrollLock(isOpen)

    const adminNavItems = useMemo((): InternalNavItem[] => {
        const labels: Record<string, string> = {
            "admin-home": navigationUi.adminHome,
            "admin-reports": navigationUi.reportedItems,
            "admin-feedback": navigationUi.userFeedback,
        }

        return ADMIN_NAV_ITEMS.map((item) => ({
            ...item,
            label: labels[item.id] ?? item.id,
        }))
    }, [navigationUi.adminHome, navigationUi.reportedItems, navigationUi.userFeedback])

    const resourceNavItems = useMemo((): ExternalNavItem[] => {
        const labels: Record<string, string> = {
            "about-societea": navigationUi.aboutSocietea,
            "get-help": navigationUi.getHelp,
            "build-societea": navigationUi.buildSocietea,
        }

        return RESOURCE_NAV_ITEMS.map((item) => ({
            ...item,
            label: labels[item.id] ?? item.id,
        }))
    }, [navigationUi.aboutSocietea, navigationUi.getHelp, navigationUi.buildSocietea])

    const privacyNavItems = useMemo((): NavItem[] => {
        const labels: Record<string, string> = {
            "content-policy": navigationUi.contentPolicy,
            "privacy-policy": navigationUi.privacyPolicy,
            "user-agreement": navigationUi.userAgreement,
        }

        return PRIVACY_NAV_ITEMS.map((item) => ({
            ...item,
            label: labels[item.id] ?? item.id,
        }))
    }, [navigationUi.contentPolicy, navigationUi.privacyPolicy, navigationUi.userAgreement])

    const isSignedIn = Boolean(currentUser)

    const primaryNavWithTasks = useMemo((): NavItem[] => {
        const primaryLabels: Record<string, string> = {
            home: navigationUi.home,
            graph: navigationUi.graphExplorer,
            communitea: navigationUi.communitea,
            feedback: navigationUi.feedback,
        }
        const localizedPrimaryItems: InternalNavItem[] = PRIMARY_NAV_ITEMS.map((item) => ({
            ...item,
            label: primaryLabels[item.id] ?? item.id,
        }))
        // Tasks is an auth-only surface (the page itself hard-requires a
        // signed-in viewer via `RequireAuth`), so the nav entry is hidden
        // for signed-out visitors to avoid advertising a page they cannot use.
        const taskEntry: InternalNavItem | null = isSignedIn
            ? {
                  id: "tasks",
                  label: taskUi.navTasks,
                  to: "/tasks",
                  renderIcon: () => <ListTodo size={20} />,
              }
            : null
        const adBreakItem: ExternalNavItem = {
            id: "ad-break",
            label: storyUi.navAdBreak,
            href: getAdBreakHref(),
            renderIcon: () => <Clapperboard size={20} />
        }
        const items: NavItem[] = [localizedPrimaryItems[0]]
        if (taskEntry) {
            items.push(taskEntry)
        }
        items.push(...localizedPrimaryItems.slice(1), adBreakItem)
        return items
    }, [
        isSignedIn,
        navigationUi.communitea,
        navigationUi.feedback,
        navigationUi.graphExplorer,
        navigationUi.home,
        storyUi.navAdBreak,
        taskUi.navTasks,
    ])

    useEffect(() => {
        const loadRecentIssues = () => {
            const recent = getRecentIssues()
            setRecentIssues(recent)
        }

        const loadTopIssues = async () => {
            try {
                if (!currentUser) {
                    setTopIssues([])
                    return
                }
                const rankingsResponse = await getIssueRankings()
                setTopIssues(rankingsResponse.data.slice(0, 10))
            } catch (error) {
                setTopIssues([])
            }
        }

        loadRecentIssues()
        loadTopIssues()

        if (isOpen) {
            loadRecentIssues()
            loadTopIssues()
        }
    }, [isOpen, currentUser])

    useEffect(() => {
        if (!isOpen) {
            return
        }

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key !== "Escape") {
                return
            }
            const syntheticEscapeEvent = { type: "keydown", key: "Escape" } as React.KeyboardEvent
            toggleDrawer(false)(syntheticEscapeEvent)
        }

        window.addEventListener("keydown", handleEscapeKey)
        return () => window.removeEventListener("keydown", handleEscapeKey)
    }, [isOpen, toggleDrawer])

    const closeDrawerWithSyntheticClick = () => {
        const syntheticClickEvent = { type: "click" } as React.MouseEvent
        toggleDrawer(false)(syntheticClickEvent)
    }

    const handleNavigation = (to: string) => {
        navigate(to)
        closeDrawerWithSyntheticClick()
        onClick()
    }

    const renderNavItem = (item: NavItem, variant: NavVariant) => {
        const defaultPaddingClass = variant === "mobile" ? "px-2 py-1.5" : "px-4 py-2"
        const itemPaddingClassName =
            variant === "mobile"
                ? item.mobilePaddingClassName ?? defaultPaddingClass
                : item.desktopPaddingClassName ?? defaultPaddingClass
        const gapClassName = item.gapClassName ?? "gap-3"
        const className = `soc-side-nav-menu-item flex items-center ${gapClassName} ${itemPaddingClassName} menu-item-alt`

        const icon = item.renderIcon()
        const content = (
            <>
                {icon}
                <span>{item.label}</span>
                {item.id === "graph" && 
                    <span className="relative top-2 right-3 items-center">
                        <span className="bg-soc-primary text-white rounded-full p-0.5 block absolute bottom-1 left-1">
                            <FlaskConical className="size-3" />
                        </span>
                    </span>
                }
            </>
        )

        if (isExternalNavItem(item)) {
            return (
                <a key={item.id} href={item.href} className={className}>
                    {content}
                </a>
            )
        }

        return (
            <button key={item.id} type="button" onClick={() => handleNavigation(item.to)} className={className}>
                {content}
            </button>
        )
    }

    const renderRecentSection = () => {
        if (recentIssues.length === 0) {
            return null
        }

        return (
            <div className="flex flex-col">
                <DetailPanel
                    title={navigationUi.recentSection}
                    defaultOpen={true}
                    className="bg-transparent mt-0 p-0"
                    titleClassName="px-2 text-gray-500 dark:text-gray-400 font-light"
                >
                    <div className="flex flex-col gap-1 max-h-64 overflow-y-auto scrollbar-hide">
                        {recentIssues.slice(0, 10).map((recentIssue, index) => {
                            const issueEntity = recentIssue.issue ?? buildFallbackIssueEntity(recentIssue)

                            return (
                                <div
                                    key={`recent-${recentIssue.id}-${index}`}
                                    className="hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon rounded-lg px-1 py-1.5 cursor-pointer"
                                    onClick={() => handleNavigation(`/issue/${recentIssue.cname}`)}
                                >
                                    <EntityAvatar
                                        entity={issueEntity}
                                        entityType="issue"
                                        entityId={recentIssue.id}
                                        entityChip
                                        showCloseChip
                                        onEntityChipClose={() => {
                                            try {
                                                removeRecentIssue(recentIssue.id)
                                                setRecentIssues(getRecentIssues())
                                            } catch (error) {
                                                console.error("Error removing recent issue from sidebar:", error)
                                            }
                                        }}
                                        showLink
                                        className="min-w-[120px] max-w-[216px] text-xs pr-0"
                                    />
                                </div>
                            )
                        })}
                    </div>
                </DetailPanel>
                <div className="my-2 list-divider recent-section" />
            </div>
        )
    }

    const renderTopIssuesSection = () => {
        if (!currentUser || topIssues.length === 0) {
            return null
        }

        return (
            <div className="flex flex-col">
                <DetailPanel
                    title={navigationUi.prioriteaSection}
                    defaultOpen={true}
                    className="bg-transparent mt-0 p-0"
                    titleClassName="px-2 text-gray-500 dark:text-gray-400 font-light"
                >
                    <div className="flex flex-col gap-1 max-h-64 overflow-y-auto scrollbar-hide">
                        {topIssues.map((ranking, index) => {
                            const issue = ranking.issue
                            if (!issue || !issue.cname) {
                                return null
                            }

                            return (
                                <div
                                    key={`top-${ranking.id}-${index}`}
                                    className="hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon rounded-lg px-1 py-1.5 cursor-pointer"
                                    onClick={() => handleNavigation(`/issue/${issue.cname}`)}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="flex-grow min-w-0">
                                            <EntityAvatar
                                                entityType="issue"
                                                entityId={issue.id}
                                                entityChip
                                                showCloseChip={false}
                                                showLink
                                                badgeText={`#${ranking.ranking}`}
                                                className="min-w-[120px] max-w-[216px] text-xs pr-0"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </DetailPanel>
                <div className="my-2 list-divider top-section" />
            </div>
        )
    }

    const renderSection = (title: string, items: ReadonlyArray<NavItem>, variant: NavVariant) => (
        <div className="soc-side-nav-link-group flex flex-col px-2">
            <span className="soc-side-nav-section-title px-2 py-2 text-gray-500 dark:text-gray-400 font-light">
                {title}
            </span>
            {items.map((item) => renderNavItem(item, variant))}
        </div>
    )

    const renderNavContent = (variant: NavVariant) => {
        const footerClassName =
            variant === "mobile"
                ? "mt-auto flex justify-center px-2 py-2 text-[10px] text-gray-500 hover:underline"
                : "internal-link flex justify-center px-2 py-2 text-[10px] font-light bg-transparent hover:underline mt-auto"
        const footerText = navigationUi.footer.replace("{year}", String(new Date().getFullYear()))

        return (
            <div className="h-full flex flex-col">
                <nav className="flex flex-col pt-2 px-2 text-sm font-light text-eoe-gray dark:text-eoe-dark-text-2 overflow-y-auto scrollbar-hide flex-1 min-h-0">
                    {currentUser ? (
                        <div className="grid grid-cols-1 gap-2 px-2 pb-2">
                            <div
                                className="mx-2 flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-xs font-semibold text-amber-700 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-300"
                                title={awardBudgetTitle}
                                aria-label={awardBudgetTitle}
                            >
                                <span className="flex items-center gap-1.5">
                                    <Award className="size-4" aria-hidden />
                                    <span>{storyUi.awardGivingBudgetLabel}</span>
                                </span>
                                <span className="tabular-nums">{awardBudget}</span>
                            </div>
                            <div
                                className="mx-2 flex items-center justify-between rounded-xl border border-sky-200 bg-sky-50/80 px-3 py-2 text-xs font-semibold text-sky-700 dark:border-sky-800/70 dark:bg-sky-950/40 dark:text-sky-300"
                                title={promotionCreditsTitle}
                                aria-label={promotionCreditsTitle}
                            >
                                <span className="flex items-center gap-1.5">
                                    <Clapperboard className="size-4" aria-hidden />
                                    <span>{storyUi.promotionCreditsLabel}</span>
                                </span>
                                <span className="tabular-nums">{promotionCredits}</span>
                            </div>
                        </div>
                    ) : null}
                    <div className="flex flex-col px-2">{primaryNavWithTasks.map((item) => renderNavItem(item, variant))}</div>

                    <div className="my-2 list-divider" />

                    {renderRecentSection()}
                    {renderTopIssuesSection()}

                    {Boolean(currentUser?.is_admin) && (
                        <div className="flex flex-col px-2">
                            <span className="px-2 py-2 text-gray-500 dark:text-gray-400 font-light">{navigationUi.adminSection}</span>
                            {adminNavItems.map((item) => renderNavItem(item, variant))}
                            <div className="my-2 list-divider" />
                        </div>
                    )}

                    {renderSection(navigationUi.resourcesSection, resourceNavItems, variant)}
                    <div className="my-2 list-divider" />
                    {renderSection(navigationUi.privacySafetySection, privacyNavItems, variant)}
                </nav>

                <a href="https://www.societea.io" className={footerClassName}>
                    {footerText}
                </a>
            </div>
        )
    }

    const sideNavPanelStyle: React.CSSProperties = {
        top: `${APP_BAR_HEIGHT_PX}px`,
        height: `calc(100% - ${APP_BAR_HEIGHT_PX}px)`,
        zIndex: LAYER_TOKENS.appBar - 1,
    }

    const sideNavOverlayStyle: React.CSSProperties = {
        top: `${APP_BAR_HEIGHT_PX}px`,
        zIndex: LAYER_TOKENS.appBar - 2,
    }

    return (
        <div>
            <aside
                style={sideNavPanelStyle}
                className="soc-side-nav-persistent hidden lg:block fixed left-0 w-[260px] bg-white dark:bg-eoe-dark-bg transition-colors duration-200 border-r border-r-eoe-border-main dark:border-r-eoe-dark-border-main"
            >
                {renderNavContent("desktop")}
            </aside>

            <button
                type="button"
                aria-label={navigationUi.closeSideNavigation}
                aria-hidden={!isOpen}
                tabIndex={isOpen ? 0 : -1}
                onClick={toggleDrawer(false)}
                style={sideNavOverlayStyle}
                className={`soc-side-nav-mobile-overlay fixed inset-x-0 bottom-0 border-0 m-0 p-0 bg-black/50 transition-opacity duration-200 lg:hidden ${
                    isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
            />

            <aside
                role="dialog"
                aria-modal={isOpen ? true : undefined}
                aria-hidden={!isOpen}
                aria-label={navigationUi.sideNavigation}
                onClick={(event) => event.stopPropagation()}
                style={sideNavPanelStyle}
                className={`soc-side-nav-mobile-panel fixed left-0 w-[240px] bg-white dark:bg-eoe-dark-bg transition-transform duration-200 ease-out lg:hidden ${
                    isOpen ? "translate-x-0 visible" : "-translate-x-full pointer-events-none invisible"
                }`}
            >
                {renderNavContent("mobile")}
            </aside>
        </div>
    )
}

export default SideNavBar